import React, { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Chip, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { BundleOffer } from '~/services/AccommodationService';

import EditBundleOfferDialog from './EditBundleOfferDialog';

interface Props {
  bundleId: string;
  offers: Array<BundleOffer>;
}

export default function BundleOffersSection({ bundleId, offers }: Props) {
  const [editingOffer, setEditingOffer] = useState<BundleOffer | null>(null);

  if (offers.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No offers associated with this bundle.
      </Typography>
    );
  }

  const columns: Array<GridColDef> = [
    {
      field: 'entityType',
      headerName: 'Type',
      width: 130,
      renderCell: ({ value }) => <Chip label={value} size="small" variant="outlined" />,
    },
    {
      field: 'entityId',
      headerName: 'Entity ID',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value, row) => row.entityId ?? '\u2014',
    },
    {
      field: 'propertyId',
      headerName: 'Property ID',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value, row) => row.propertyId ?? '\u2014',
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      valueGetter: (_value, row) => `${row.currency} ${row.price?.toFixed(2) ?? '0.00'}`,
    },
    {
      field: 'productId',
      headerName: 'Ticket ID',
      flex: 1,
      minWidth: 150,
      valueGetter: (_value, row) => row.productId ?? '\u2014',
    },
    {
      field: 'experienceName',
      headerName: 'Experience Name',
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) => row.experienceName ?? '\u2014',
    },
    {
      field: 'experienceDescription',
      headerName: 'Inclusion Text',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value, row) => row.experienceDescription ?? '\u2014',
    },
    {
      field: 'icon',
      headerName: 'Icon',
      width: 120,
      valueGetter: (_value, row) => row.icon ?? '\u2014',
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) =>
        row.entityType === 'EXPERIENCE' ? (
          <IconButton size="small" onClick={() => setEditingOffer(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Properties and experiences included in this bundle. Offers are managed via Tags.
      </Typography>
      <DataGrid
        rows={offers}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        hideFooter={offers.length <= 10}
        pageSizeOptions={[10]}
      />
      {editingOffer && (
        <EditBundleOfferDialog
          key={editingOffer.id}
          bundleId={bundleId}
          offer={editingOffer}
          open={!!editingOffer}
          onClose={() => setEditingOffer(null)}
        />
      )}
    </Stack>
  );
}
